import React, { useContext, useMemo } from 'react';

import { RendererExtensionContext } from '@confluence/content-renderer-extension-context/entry-points/RendererExtensionContext.ts';
import { ClassicEditorContext } from '@confluence/editor-features/entry-points/ClassicEditorContext.ts';
import {
	createNestedRendererData,
	type NestedAdfRendererWrapper,
	type NestedAdfRendererWrapperProps,
} from '@confluence/macro-utils/entry-points/nestedAdfRenderer.ts';

type NestedAdfRendererWrapperComponentProps = NestedAdfRendererWrapperProps & {
	Renderer: React.FC<any>;
	appearance?: string;
	rendererExtensionOptions: React.ContextType<typeof RendererExtensionContext>;
};

export type NestedAdfRendererWrapperProviderProps = {
	children: (RendererWrapper: NestedAdfRendererWrapper) => React.ReactNode;
	Renderer: React.FC<any>;
	appearance?: string;
};

const NestedAdfRendererWrapperComponent = ({
	Renderer,
	appearance,
	rendererExtensionOptions,
	adf,
	targetContentId,
	targetSpaceKey,
	mediaToken,
}: NestedAdfRendererWrapperComponentProps) => {
	const rendererAppearance = appearance === 'full-page' ? 'full-page' : 'full-width';
	const rendererData = useMemo(() => createNestedRendererData(adf), [adf]);

	return (
		<RendererExtensionContext.Provider value={rendererExtensionOptions}>
			<Renderer
				contentId={targetContentId}
				spaceKey={targetSpaceKey}
				data={rendererData}
				appearance={rendererAppearance}
				hasInlineComments={false}
				mediaToken={mediaToken}
			/>
		</RendererExtensionContext.Provider>
	);
};

export const NestedAdfRendererWrapperProvider = ({
	children,
	Renderer,
	appearance,
}: NestedAdfRendererWrapperProviderProps) => {
	const { getExtensionHandlers, populateExtensionProviders } = useContext(ClassicEditorContext);

	const contextRendererExtensionOptions = useMemo(
		() => ({
			getExtensionHandlers,
			populateExtensionProviders,
		}),
		[getExtensionHandlers, populateExtensionProviders],
	);

	const RendererWrapper = useMemo(
		() => (props: NestedAdfRendererWrapperProps) => (
			<NestedAdfRendererWrapperComponent
				{...props}
				Renderer={Renderer}
				appearance={appearance}
				rendererExtensionOptions={contextRendererExtensionOptions}
			/>
		),
		[appearance, Renderer, contextRendererExtensionOptions],
	);

	return <>{children(RendererWrapper)}</>;
};
